// Considered: ButtonAtom — used directly for the operator toggle (variant="text",
// size="small", restyled by the `--control` tone class), so the index-1 joiner
// and FilterGroupNodeMolecule's compact all/any chip are one control, not two
// hand-rolled buttons. Considered: SegmentedControlAtom — rejected for the
// toggle because it renders every option side by side; this control is a
// single word that flips between two operators in the row gutter (and in a
// narrow group header) where a two-segment strip has no room
// (advanced-filters-v2.jsx `AFJoiner` + `AFGroupNode` compact chip). The
// read-only echo stays a plain `<span>`: it is text, not a control. See spec
// Reuse Audit (`FilterJoinerAtom` row).

import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../Button/ButtonAtom';
import './FilterJoinerAtom.scss';

type FilterOperator = 'and' | 'or';

interface FilterJoinerAtomCommonProps {
  /** The operator this word echoes (the parent group's own, for a joiner). */
  parentOperator: FilterOperator;
  /**
   * Toggles the operator. A joiner only consumes it at `index` 1 — every other
   * position stays a read-only echo regardless of whether this is supplied, so
   * a host can pass the same handler to every row's joiner without checking
   * position itself. A quantifier is a control whenever this is supplied.
   * Omit it (e.g. a read-only tree) and every variant renders as plain text.
   */
  onSetOp?: (op: FilterOperator) => void;
  className?: string;
  /**
   * Parity instrumentation — applied to whichever element actually renders
   * (the control button, or the read-only echo span), so a host that needs
   * the two states to answer to distinct contract ids can tell them apart.
   * A caller-supplied `data-datum` on a wrapping element can't do this: it
   * would tag both the button and the span the same, and (worse) tag a
   * `<span>` wrapper even when the rendered content underneath is the button.
   */
  datumId?: string;
  /** Parity instrumentation — `data-kind` on the rendered element, beside `datumId`. */
  datumKind?: string;
}

interface FilterJoinerVariantProps extends FilterJoinerAtomCommonProps {
  /**
   * `'joiner'` (default): the gutter word between a group's rows —
   * "where" / "and" / "or", a control only at index 1.
   */
  variant?: 'joiner';
  /** Position of this row within its parent group's children. */
  index: number;
}

interface FilterQuantifierVariantProps extends FilterJoinerAtomCommonProps {
  /**
   * `'quantifier'`: the same operator read as "all" / "any" — the compact
   * group header of a narrow tree (`FilterGroupNodeMolecule` `narrow`,
   * nested). Position-free: a control whenever `onSetOp` is supplied.
   */
  variant: 'quantifier';
  index?: never;
}

export type FilterJoinerAtomProps = FilterJoinerVariantProps | FilterQuantifierVariantProps;

const otherOperator = (op: FilterOperator): FilterOperator => (op === 'or' ? 'and' : 'or');

/**
 * Echo of a filter row's parent group operator: "where" for the first row
 * (so a one-row filter still scans as a sentence), then "and"/"or" for every
 * row after. The joiner at index 1 is the flat-mode all/any control — the
 * one place a group's operator can be set without its own header bar
 * (`UnifiedFilterTreeMolecule` flat mode: no bar until a group exists).
 * Every other position never renders an editing affordance — the group's
 * operator is otherwise changed at the group header only
 * (advanced-filters-v2.jsx `JoinerCell`).
 *
 * The `quantifier` variant is that same control worded "all"/"any", used as
 * a narrow tree's compact group header — one implementation, so the two
 * operator toggles in a tree can never drift apart in look or behaviour.
 */
const FilterJoinerAtom: React.FC<FilterJoinerAtomProps> = (props) => {
  const { parentOperator, onSetOp, className = '', datumId, datumKind } = props;
  const { t } = useTranslation();
  const quantifier = props.variant === 'quantifier';
  const isFirst = !quantifier && props.index === 0;
  const isControl = !!onSetOp && (quantifier || props.index === 1);

  const wordFor = (op: FilterOperator): string => {
    if (quantifier) {
      return op === 'or' ? t('filters.filterGroups.compactAny') : t('filters.filterGroups.compactAll');
    }
    return op === 'or' ? t('filters.joiner.or') : t('filters.joiner.and');
  };
  const word = isFirst ? t('filters.joiner.where') : wordFor(parentOperator);

  const classes = [
    'filter-joiner-atom',
    isFirst ? '' : `filter-joiner-atom--${parentOperator}`,
    quantifier ? 'filter-joiner-atom--quantifier' : '',
    isControl ? 'filter-joiner-atom--control' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (isControl) {
    const next = otherOperator(parentOperator);
    return (
      <ButtonAtom
        type="button"
        variant="text"
        size="small"
        className={classes}
        onClick={(e) => {
          e.stopPropagation();
          onSetOp?.(next);
        }}
        // The name leads with the visible word (WCAG 2.5.3) and states both the
        // current operator and what activating the control does — the action
        // must not live only in the hover `title` (ux-craft L2). The current
        // value is carried by the name itself rather than `aria-pressed`: this
        // is a two-value switch whose visible word changes, not an on/off
        // button, so a pressed state would misreport it.
        aria-label={t('filters.joiner.toggleLabel', { current: word, next: wordFor(next) })}
        title={parentOperator === 'or' ? t('filters.joiner.toggleToAll') : t('filters.joiner.toggleToAny')}
        data-datum={datumId}
        data-kind={datumKind}
      >
        {word}
      </ButtonAtom>
    );
  }

  return (
    <span className={classes} data-datum={datumId} data-kind={datumKind}>
      {word}
    </span>
  );
};

FilterJoinerAtom.displayName = 'FilterJoinerAtom';

export default FilterJoinerAtom;
